import React, { useMemo, useState, useEffect } from "react";
import { Calendar, User, Mail, Phone, FileText, Clock, Search } from "lucide-react";
import { StatPill, EmptyState } from "./ui/DashboardUI";
import AppointmentChat from "../telehealth/AppointmentChat";

const initialPageSize = 8;
const pageStep = 8;

const AppointmentManagement = ({
  bookings,
  stats,
  statusFilter,
  onChangeStatusFilter,
  onUpdateAppointmentStatus,
  getStatusColor,
  formatDate,
  formatTime,
}) => {
  const [query, setQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(initialPageSize);

  // floating chat
  const [chatBooking, setChatBooking] = useState(null);

  // decline modal state
  const [declineBooking, setDeclineBooking] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [declineError, setDeclineError] = useState("");

  useEffect(() => setVisibleCount(initialPageSize), [statusFilter, query]);

  const matchesQuery = (b, q) => {
    if (!q) return true;
    const hay = [
      b.patientName,
      b.patientEmail,
      b.service,
      b.reason,
      b.notes,
      b.patientInfo?.phone,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q.toLowerCase());
  };

  const filteredBookings = useMemo(() => {
    const byStatus = statusFilter
      ? bookings.filter((b) => b.status === statusFilter)
      : bookings;
    return byStatus.filter((b) => matchesQuery(b, query));
  }, [bookings, statusFilter, query]);

  const paged = filteredBookings.slice(0, visibleCount);
  const canLoadMore = filteredBookings.length > visibleCount;

  const counts = useMemo(
    () => ({
      pending: bookings.filter((b) => b.status === "pending").length,
      confirmed: bookings.filter((b) => b.status === "confirmed").length,
      completed: bookings.filter((b) => b.status === "completed").length,
      cancelled: bookings.filter((b) => b.status === "cancelled").length,
    }),
    [bookings]
  );

  const clickStat = (status) =>
    onChangeStatusFilter(statusFilter === status ? "" : status);

  // confirm decline from modal
  const handleConfirmDecline = () => {
    if (!declineBooking) return;

    const reason = declineReason.trim();
    if (!reason) {
      setDeclineError("Please write a short reason for declining.");
      return;
    }

    // parent must support third param: cancelReason
    onUpdateAppointmentStatus(declineBooking.id, "cancelled", reason);

    setDeclineBooking(null);
    setDeclineReason("");
    setDeclineError("");
  };

  const handleCancelDecline = () => {
    setDeclineBooking(null);
    setDeclineReason("");
    setDeclineError("");
  };

  return (
    <>
      <section className="am-wrap">
        <style>{css}</style>

        {/* Header */}
        <div className="am-hdr">
          <Calendar className="am-hdrIcon" />
          <h3 className="am-hdrTitle">Appointment Management</h3>
        </div>

        {/* Filter + Search */}
        <div className="am-toolbar">
          <div className="am-searchWrap">
            <Search className="am-searchIcon" size={16} />
            <input
              className="am-input am-search"
              type="search"
              placeholder="Search by patient, email, service, reason, notes…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <select
            className="am-input am-select"
            value={statusFilter}
            onChange={(e) => onChangeStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Summary pills */}
        <div className="am-stats">
          <button
            className="am-pillBtn"
            aria-pressed={statusFilter === "pending"}
            onClick={() => clickStat("pending")}
            type="button"
          >
            <StatPill
              color="#fef3c7"
              textColor="#92400e"
              label="Pending"
              value={counts.pending}
            />
          </button>
          <button
            className="am-pillBtn"
            aria-pressed={statusFilter === "confirmed"}
            onClick={() => clickStat("confirmed")}
            type="button"
          >
            <StatPill
              color="#dcfce7"
              textColor="#166534"
              label="Confirmed"
              value={counts.confirmed}
            />
          </button>
          <button
            className="am-pillBtn"
            aria-pressed={statusFilter === "completed"}
            onClick={() => clickStat("completed")}
            type="button"
          >
            <StatPill
              color="#dbeafe"
              textColor="#1e40af"
              label="Completed"
              value={counts.completed}
            />
          </button>
          <button
            className="am-pillBtn"
            aria-pressed={statusFilter === "cancelled"}
            onClick={() => clickStat("cancelled")}
            type="button"
          >
            <StatPill
              color="#fee2e2"
              textColor="#991b1b"
              label="Cancelled"
              value={counts.cancelled}
            />
          </button>
        </div>

        {/* List */}
        <div className="am-list">
          {paged.length === 0 ? (
            <EmptyState
              title={
                query
                  ? "No results match your search"
                  : statusFilter
                  ? `No ${statusFilter} appointments`
                  : "No appointment requests yet"
              }
              subtitle={
                query
                  ? "Try a different keyword or clear the search"
                  : statusFilter
                  ? "Try changing the filter to see other appointments"
                  : "Patient requests will appear here"
              }
            />
          ) : (
            <>
              {paged.map((booking) => {
                const statusStyle = getStatusColor(booking.status);
                const phone = booking.patientInfo?.phone;

                return (
                  <article
                    key={booking.id}
                    className="am-card"
                    aria-label="Appointment"
                  >
                    {/* TOP ROW */}
                    <div className="am-rowTop">
                      <div className="am-avatar" aria-hidden>
                        <User style={{ width: 18, height: 18 }} />
                      </div>

                      <div className="am-id">
                        <h3 className="am-name">{booking.patientName}</h3>
                        <div className="am-inline">
                          <span className="am-meta">
                            <Mail style={{ width: 12, height: 12 }} />
                            {booking.patientEmail}
                          </span>
                          {phone && (
                            <span className="am-meta am-meta--sub">
                              <Phone style={{ width: 12, height: 12 }} />
                              {phone}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="am-statusWrap">
                        <span
                          className="am-status"
                          style={{
                            ...statusStyle,
                            borderColor:
                              statusStyle?.backgroundColor ?? "#e0e0e0",
                          }}
                        >
                          {booking.status}
                        </span>
                        {booking.appointmentType && (
                          <span className="am-type">
                            {booking.appointmentType === "in-person"
                              ? "In-person"
                              : booking.appointmentType === "video-call"
                              ? "Video"
                              : "Phone"}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* MIDDLE ROW */}
                    <div className="am-rowMid">
                      <div>
                        <p className="am-service">{booking.service}</p>
                        <p className="am-reason">
                          <strong>Reason:</strong>{" "}
                          {booking.reason?.length > 140
                            ? booking.reason.slice(0, 140).trimEnd() + "…"
                            : booking.reason || "—"}
                        </p>
                      </div>
                      {booking.servicePrice && (
                        <span className="am-price">
                          {booking.servicePrice}
                        </span>
                      )}
                    </div>

                    {/* META ROW */}
                    <div className="am-rowMeta">
                      <span className="am-meta">
                        <Calendar style={{ width: 12, height: 12 }} />
                        {booking.scheduledDate
                          ? new Date(
                              booking.scheduledDate
                            ).toLocaleDateString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            })
                          : "Date TBD"}
                      </span>

                      {booking.scheduledTime && (
                        <span className="am-meta">
                          <Clock style={{ width: 12, height: 12 }} />
                          {formatTime(booking.scheduledTime)}
                        </span>
                      )}

                      <span className="am-meta">
                        <FileText style={{ width: 12, height: 12 }} />
                        Requested {formatDate(booking.date)}
                      </span>
                    </div>

                    {/* NOTES */}
                    {booking.notes && (
                      <p className="am-notes">
                        <strong>Notes:</strong>{" "}
                        {booking.notes.length > 180
                          ? booking.notes.slice(0, 180).trimEnd() + "…"
                          : booking.notes}
                      </p>
                    )}

                    {/* DECLINE REASON (doctor view) */}
                    {booking.status === "cancelled" &&
                      booking.cancelReason && (
                        <p className="am-cancelReason">
                          <strong>Decline reason:</strong>{" "}
                          {booking.cancelReason}
                        </p>
                      )}

                    {/* ACTIONS ROW */}
                    <div className="am-actions">
                      {booking.status === "pending" && (
                        <>
                          <button
                            type="button"
                            className="am-btn am-btn--ok"
                            onClick={() =>
                              onUpdateAppointmentStatus(
                                booking.id,
                                "confirmed"
                              )
                            }
                          >
                            ✓ Confirm
                          </button>
                          <button
                            type="button"
                            className="am-btn am-btn--danger"
                            onClick={() => {
                              setDeclineBooking(booking);
                              setDeclineReason("");
                              setDeclineError("");
                            }}
                          >
                            ✗ Decline
                          </button>
                        </>
                      )}

                      {/* Chat button */}
                      <button
                        type="button"
                        className="am-btn am-btn--secondary"
                        onClick={() => setChatBooking(booking)}
                      >
                        💬 Chat
                      </button>
                    </div>
                  </article>
                );
              })}

              {canLoadMore && (
                <div className="am-loadMoreWrap">
                  <button
                    type="button"
                    className="am-loadMore"
                    onClick={() => setVisibleCount((c) => c + pageStep)}
                  >
                    Load more ({filteredBookings.length - paged.length} more)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Floating telehealth chat window */}
      {chatBooking && (
        <div className="telehealth-chat-container">
          <AppointmentChat
            appointmentId={chatBooking.id}
            doctorId={chatBooking.doctorId}
            patientId={chatBooking.patientId}
            appointmentStatus={chatBooking.status}
            doctorName={chatBooking.doctorName}
            patientName={chatBooking.patientName}
            serviceName={chatBooking.service}
            onClose={() => setChatBooking(null)}
          />
        </div>
      )}

      {/* Decline reason modal */}
      {declineBooking && (
        <div className="am-modalOverlay" onClick={handleCancelDecline}>
          <div
            className="am-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3 className="am-modalTitle">Decline appointment</h3>
            <p className="am-modalSubtitle">
              Please write a short reason. The patient may see this message in
              their portal/email.
            </p>

            <div className="am-modalBody">
              <div className="am-modalLabel">
                Appointment with{" "}
                <strong>{declineBooking.patientName}</strong>
              </div>
              <textarea
                className="am-modalTextarea"
                placeholder="Example: I’m not available at this time. Please reschedule for another day or choose a different time slot."
                value={declineReason}
                onChange={(e) => {
                  setDeclineReason(e.target.value);
                  setDeclineError("");
                }}
              />
              {declineError && (
                <div className="am-modalError">{declineError}</div>
              )}
            </div>

            <div className="am-modalActions">
              <button
                type="button"
                className="am-modalBtn am-modalBtn--secondary"
                onClick={handleCancelDecline}
              >
                Cancel
              </button>
              <button
                type="button"
                className="am-modalBtn am-modalBtn--danger"
                onClick={handleConfirmDecline}
              >
                Confirm decline
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const css = `
/* wrapper uses parent section padding; no inner panel look */
.am-wrap { padding: 0; }

/* Flat subheader */
.am-hdr { display:flex; align-items:center; gap:10px; margin: 4px 0 10px; }
.am-hdrIcon { width:20px; height:20px; color:#0a66c2; }
.am-hdrTitle { margin:0; font-size:16px; font-weight:700; color:#1f2328; }

/* Inputs */
.am-input {
  background:#fff; border:1px solid #e0e0e0; border-radius:10px; box-shadow:none;
  transition: border-color .15s ease, box-shadow .15s ease;
}
.am-input:focus { outline:none; border-color:#0a66c2; box-shadow:0 0 0 3px rgba(10,102,194,.15); }

/* Toolbar */
.am-toolbar { display:flex; gap:10px; align-items:center; justify-content:space-between; padding: 6px 0 0; flex-wrap:wrap; }
.am-searchWrap { position: relative; flex: 1 1 320px; min-width: 220px; }
.am-search { width: 100%; padding: 10px 12px 10px 36px; font-size: 13px; }
.am-searchIcon { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:#9ca3af; }
.am-select { padding: 10px 12px; font-size: 12px; }

/* Stat pills */
.am-stats { display:grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap:12px; padding: 12px 0 8px; }
.am-pillBtn { border:none; background:transparent; padding:0; border-radius:12px; cursor:pointer; }
.am-pillBtn[aria-pressed="true"] { transform: translateY(-1px); }

/* List: no inner scrollbar */
.am-list { padding-top: 6px; }

/* Cards */
.am-card {
  display:flex; flex-direction:column; gap:10px;
  background:#fff; border:1px solid #e0e0e0; border-radius:12px; padding:14px; margin-bottom:12px;
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
}
@media (hover:hover) and (pointer:fine){
  .am-card:hover { transform: translateY(-1px); box-shadow: 0 8px 24px -18px rgba(0,0,0,.25); border-color: rgba(10,102,194,.25); }
}

/* Header row */
.am-rowTop { display:flex; align-items:center; gap:12px; }
.am-avatar { width:40px; height:40px; border-radius:10px; flex:0 0 40px; display:flex; align-items:center; justify-content:center; color:#fff; background:#0a66c2; }
.am-id { flex:1 1 auto; min-width:0; }
.am-name { margin:0; font-weight:700; color:#0f172a; font-size:14.5px; line-height:1.2; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.am-inline { display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-top:4px; }
.am-meta { display:flex; align-items:center; gap:6px; color:#6b7280; font-size:12px; }
.am-meta--sub { color:#9ca3af; }
.am-statusWrap { display:flex; flex-direction:column; align-items:flex-end; gap:6px; margin-left:auto; }
.am-status { font-size:11.5px; font-weight:700; padding:4px 10px; border-radius:999px; border:1px solid #e0e0e0; }
.am-type { font-size:10.5px; font-weight:700; color:#6b7280; background:#f3f4f6; padding:3px 8px; border-radius:999px; }

/* Middle row */
.am-rowMid { display:flex; align-items:flex-start; justify-content:space-between; gap:10px; padding-left: 52px; }
.am-service { margin:0 0 4px 0; font-weight:700; color:#0f172a; font-size:13px; }
.am-reason { margin:0; color:#6b7280; font-size:12.5px; }
.am-price { font-size:12px; font-weight:700; color:#05603a; background:#e7f5ee; padding:6px 10px; border-radius:10px; height: fit-content; }

/* Meta + notes */
.am-rowMeta { display:flex; align-items:center; gap:16px; color:#9ca3af; font-size:12px; padding-left: 52px; margin-top:6px; }
.am-notes { margin:8px 0 0 0; padding:8px 10px; background:#f9fafb; border-radius:10px; color:#6b7280; font-size:12px; font-style:italic; border:1px dashed #e5e7eb; padding-left: 52px; }

/* Decline reason */
.am-cancelReason {
  margin:6px 0 0 0;
  padding:8px 10px;
  background:#fef2f2;
  border-radius:10px;
  color:#991b1b;
  font-size:12px;
  border:1px dashed #fecaca;
  padding-left: 52px;
}

/* Actions */
.am-actions { display:flex; gap:8px; padding-left: 52px; padding-top: 10px; margin-top: 6px; border-top: 1px solid #f3f4f6; flex-wrap:wrap; }
.am-btn {
  font-size:12.5px; padding:8px 12px; color:#fff; border:none; border-radius:10px; cursor:pointer;
  font-weight:700; transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
  box-shadow: 0 10px 16px -12px rgba(0,0,0,.22);
}
.am-btn:focus { outline:none; box-shadow: 0 0 0 3px rgba(10,102,194,.22); }
.am-btn:hover { filter: brightness(0.98); transform: translateY(-1px); }
.am-btn--ok { background:#10b981; }
.am-btn--danger { background:#ef4444; }
.am-btn--secondary { background:#2563eb; }

/* Load more */
.am-loadMoreWrap { display:flex; justify-content:center; margin-top:8px; }
.am-loadMore {
  border-radius:999px; border:1px solid #d1d5db; padding:8px 20px; font-size:12.5px;
  background:#fff; cursor:pointer; font-weight:600; color:#374151;
}

/* Floating telehealth chat container */
.telehealth-chat-container {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 9999;
}

/* Decline modal */
.am-modalOverlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9998;
}

.am-modal {
  background: #ffffff;
  border-radius: 16px;
  padding: 18px 18px 14px;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.35);
}

.am-modalTitle {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.am-modalSubtitle {
  margin: 0 0 12px 0;
  font-size: 12.5px;
  color: #6b7280;
}

.am-modalBody {
  margin-bottom: 12px;
}

.am-modalLabel {
  font-size: 12px;
  color: #4b5563;
  margin-bottom: 6px;
}

.am-modalTextarea {
  width: 100%;
  min-height: 90px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 13px;
  resize: vertical;
  box-sizing: border-box;
}

.am-modalTextarea:focus {
  outline: none;
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.25);
}

.am-modalError {
  margin-top: 6px;
  font-size: 11.5px;
  color: #b91c1c;
}

.am-modalActions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.am-modalBtn {
  border-radius: 999px;
  border: none;
  padding: 7px 14px;
  font-size: 12.5px;
  cursor: pointer;
  font-weight: 600;
}

.am-modalBtn--secondary {
  background: #f3f4f6;
  color: #374151;
}

.am-modalBtn--danger {
  background: #ef4444;
  color: #ffffff;
}

/* Small screens */
@media (max-width: 560px){
  .am-card { padding: 12px; }
  .am-rowMid, .am-rowMeta, .am-actions, .am-cancelReason { padding-left: 48px; }
  .am-name { font-size:14px; }
  .am-service { font-size:12.5px; }
  .am-reason { font-size:12px; }
}
`;

export default React.memo(AppointmentManagement);
